import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';
import { useContextSelector } from 'use-context-selector';
import { BucketLogData } from '@bucketplace/bucket-log';

import { EditorPlaceholder } from '../../../components/EditorPlaceholder';
import { EditorContext, useEditorInfoContext } from '../../../contexts';
import { useEditorNode } from '../../../hooks';
import { DocumentPath } from '../../../types';
import { findNode } from '../../../utils';

export interface AccordionButtonProps {
  children?: React.ReactNode;
  expanded: boolean;
  path: DocumentPath;
  action: 'expand' | 'collapse';
  logData?: BucketLogData;
  expand: () => void;
  collapse: () => void;
  onClick?: () => void;
}

export function AccordionButton(props: AccordionButtonProps) {
  const { path, action, logData } = props;
  const { onSendLog } = useEditorInfoContext();

  const { handleClickInEditor } = useEditorInitializer({
    path,
    expand: props.expand,
    collapse: props.collapse,
  });

  const handleClick = (): void => {
    if (logData) {
      onSendLog?.(logData);
    }
    handleClickInEditor?.();
  };

  if (action === 'expand') {
    return <AccordionExpandButton {...props} onClick={handleClick} />;
  }
  return <AccordionCollapseButton {...props} onClick={handleClick} />;
}

function AccordionExpandButton({
  children,
  expanded,
  expand,
  onClick,
}: AccordionButtonProps) {
  if (expanded) { return null; }

  function handleClick() {
    expand();
    onClick?.();
  }

  return (
    <Button type="button" onMouseDown={handleClick}>
      { children || <Placeholder icon={<BsPlus />} /> }
    </Button>
  );
}

function AccordionCollapseButton({
  children,
  expanded,
  collapse,
  onClick,
}: AccordionButtonProps) {
  if (!expanded) { return null; }

  function handleClick() {
    collapse();
    onClick?.();
  }

  return (
    <Button type="button" onMouseDown={handleClick}>
      { children || <Placeholder icon={<BsPlus />} /> }
    </Button>
  );
}

function useEditorInitializer({
  path,
  expand,
  collapse,
}: {
  path: DocumentPath,
  expand: () => void,
  collapse: () => void,
}) {
  const context = useContextSelector(EditorContext, (context) => context);
  const parentNode = context ? findNode(context.state.document, path.slice(0, -1)) : undefined;
  const node = context ? findNode(context.state.document, path) : undefined;

  function moveCursorToAnotherActionButton() {
    if (!context) { return; }
    let anotherButtonIndex: (number | undefined);
    if (node?.props.action === 'expand') {
      anotherButtonIndex = parentNode?.children.findIndex(
        (child) => child.type === 'accordionButton' && child.props.action === 'collapse',
      );
    } else {
      anotherButtonIndex = parentNode?.children.findIndex(
        (child) => child.type === 'accordionButton' && child.props.action === 'expand',
      );
    }
    if (typeof anotherButtonIndex !== 'undefined') {
      context?.setState((state) => ({
        ...state,
        cursor: [...path.slice(0, -1), anotherButtonIndex],
      }));
    }
  }

  // expand 버튼(or 자식)이 트리리스트에서 선택되면 아코디언 collapse
  // collapse 버튼(or 자식)이 트리리스트에서 선택되면 아코디언 expand
  const { isChildSelected } = useEditorNode(path);
  useEffect(() => {
    if (!isChildSelected) { return; }
    (node?.props.action === 'expand' ? collapse : expand)();
  }, [isChildSelected]);

  return {
    handleClickInEditor: context ? moveCursorToAnotherActionButton : undefined,
  };
}

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
`;

const Placeholder = styled(EditorPlaceholder)`
  padding: 10px 0;
`;
